import { RejectPackageModal } from '@/domains/jobs/components/RejectPackageModal';
import { RequestMoreInfoButton } from '@/domains/jobs/components/RequestMoreInfoButton';
import { RequestMoreInfoOrReplyModal } from '@/domains/jobs/components/RequestMoreInfoOrReplyModal';
import type { RequestMoreInfoOrReplyJob } from '@/domains/jobs/components/RequestMoreInfoOrReplyModal';
import {
  useAbortWorkOrder,
  useAcceptWorkOrders,
  useCancelFromAbort,
  useCancelWorkOrder,
  useEngineerEnRoute,
  useEngineerOnSite,
  useEngineeringComplete,
  useRejectWorkOrders,
  useReplanFromAbort,
  useReplyMoreInfoWorkOrders,
  useRequestMoreInfoWorkOrders,
  useValidationRequested,
} from '@/domains/jobs/hooks/useWorkOrderActions';
import { queryKeys } from '@/lib/query-client';
import { showSuccessToast } from '@/shared/notifications/toast-utils';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { cn } from '@/shared/ui/utils';
import { useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  MessageSquare,
  RefreshCcw,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';

interface StatusBarJobData {
  id: string;
  status: string;
  priority: string;
  hasResponse?: boolean;
  hasBeenResent?: boolean;
  externalId?: string | null;
  name?: string | null;
  address?: string | null;
  reqCompletionDate?: string | null;
  mirPendingDirection?: 'inbound' | 'outbound' | null;
  jobStatusName?: string | null;
}

function normalizeStatus(status: string): string {
  const lower = status.toLowerCase().trim();
  if (lower === 'more info requested') return 'more-info-requested';
  if (lower === 'in progress') return 'in-progress';
  return lower.replace(/\s+/g, '-');
}

function toTitleCase(status: string): string {
  return status
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function CancelButton({ onClick }: { onClick: () => void }) {
  return (
    <Button
      variant="outline"
      size="sm"
      className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
      onClick={onClick}
    >
      <XCircle className="w-4 h-4 mr-2" />
      Cancel
    </Button>
  );
}

function getPriorityColor(priority: string): string {
  switch (priority.toLowerCase()) {
    case 'critical':
    case 'high':
      return 'text-red-600 border-red-600 bg-red-50 dark:bg-red-950/30';
    case 'medium':
      return 'text-amber-600 border-amber-600 bg-amber-50 dark:bg-amber-950/30';
    case 'low':
      return 'text-slate-600 border-slate-600 bg-slate-50 dark:bg-slate-950/30';
    default:
      return 'text-slate-600 border-slate-600';
  }
}

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return (
        <Badge className="bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200 border-green-600">
          Completed
        </Badge>
      );
    case 'in-progress':
    case 'accepted':
      return (
        <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200 border-blue-600">
          In Progress
        </Badge>
      );
    case 'new':
      return (
        <Badge className="bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100 border-slate-600">
          New
        </Badge>
      );
    case 'reopened':
      return (
        <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200 border-blue-600">
          Reopened
        </Badge>
      );
    case 'more-info-requested':
      return (
        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200 border-amber-600">
          More Info Requested
        </Badge>
      );
    case 'rejected':
      return (
        <Badge className="bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200 border-red-600">
          Rejected
        </Badge>
      );
    case 'urgent':
      return (
        <Badge className="bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200 border-red-600">
          Urgent
        </Badge>
      );
    case 'pending':
      return (
        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200 border-amber-600">
          Pending
        </Badge>
      );
    default:
      return <Badge variant="outline">{toTitleCase(status)}</Badge>;
  }
}

export function StatusBar({ jobData }: { jobData: StatusBarJobData }) {
  const navigate = useNavigate();
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [requestInfoModalOpen, setRequestInfoModalOpen] = useState(false);
  const [acceptModalOpen, setAcceptModalOpen] = useState(false);
  const [engineeringCompleteModalOpen, setEngineeringCompleteModalOpen] = useState(false);
  const [engineeringCompleteNote, setEngineeringCompleteNote] = useState('');
  const [validationRequestedModalOpen, setValidationRequestedModalOpen] = useState(false);
  const [validationNote, setValidationNote] = useState('');
  const [validationInvoiceReference, setValidationInvoiceReference] = useState('');
  const [validationInvoiceAmount, setValidationInvoiceAmount] = useState('');
  const [abortModalOpen, setAbortModalOpen] = useState(false);
  const [abortNote, setAbortNote] = useState('');
  const [cancelFromAbortModalOpen, setCancelFromAbortModalOpen] = useState(false);
  const [replanFromAbortModalOpen, setReplanFromAbortModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const queryClient = useQueryClient();
  const acceptWorkOrders = useAcceptWorkOrders();
  const engineerEnRoute = useEngineerEnRoute();
  const engineerOnSite = useEngineerOnSite();
  const engineeringComplete = useEngineeringComplete();
  const validationRequested = useValidationRequested();
  const abortWorkOrder = useAbortWorkOrder();
  const cancelFromAbort = useCancelFromAbort();
  const replanFromAbort = useReplanFromAbort();
  const cancelWorkOrderMutation = useCancelWorkOrder();
  const rejectWorkOrders = useRejectWorkOrders();
  const requestMoreInfo = useRequestMoreInfoWorkOrders();
  const replyMoreInfo = useReplyMoreInfoWorkOrders();

  const status = normalizeStatus(jobData.status);
  const isReply = jobData.mirPendingDirection === 'inbound';
  const wos = jobData.status;
  const js = jobData.jobStatusName ?? '';
  const showCancelButton =
    (js === 'Not Started' && (wos === 'New' || wos === 'Reopened')) ||
    (js === 'Accepted' && wos === 'Ready for Planning') ||
    (js === 'On Hold' && wos === 'Duration Challenged') ||
    (js === 'Planned' && wos === 'Planned') ||
    (js === 'In Progress' &&
      (wos === 'En Route' || wos === 'On Site' || wos === 'Engineering Completed')) ||
    (js === 'Abort' && wos === 'Ready for Planning');

  const requestInfoJob: RequestMoreInfoOrReplyJob = {
    id: Number(jobData.id),
    name: jobData.name ?? null,
    address: jobData.address ?? null,
    work_order_status: jobData.status,
    req_completion_date: jobData.reqCompletionDate ?? null,
    mir_pending_direction: jobData.mirPendingDirection ?? null,
  };

  const handleRequestInfoSubmit = async (comment: string, _files: File[]) => {
    const payload = [
      {
        id: Number(jobData.id),
        externalId: jobData.externalId ?? '',
        comments: comment,
      },
    ];

    if (isReply) {
      await replyMoreInfo.mutateAsync(payload);
    } else {
      await requestMoreInfo.mutateAsync(payload);
    }
    setRequestInfoModalOpen(false);
    queryClient.invalidateQueries({ queryKey: queryKeys.jobs.detail(jobData.id) });
  };

  return (
    <>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          {getStatusBadge(status)}
          <Badge variant="outline" className={cn('gap-1', getPriorityColor(jobData.priority))}>
            <AlertCircle className="size-3 shrink-0" />
            {jobData.priority.charAt(0).toUpperCase() + jobData.priority.slice(1)} Priority
          </Badge>
          {jobData.hasResponse && (
            <Badge className="bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/30 hover:bg-green-500/20 gap-1 text-nowrap">
              <MessageSquare className="size-3 shrink-0" />
              Response Received
            </Badge>
          )}
          {jobData.hasBeenResent && (
            <Badge className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30 hover:bg-blue-500/20 gap-1 text-nowrap">
              <RefreshCcw className="size-3 shrink-0" />
              Re-sent with Info
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Action buttons for rejected and more-info-requested statuses */}
          {(status === 'rejected' || status === 'more-info-requested') && (
            <>
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                onClick={() => setAcceptModalOpen(true)}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Accept
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                onClick={() => setRejectModalOpen(true)}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </>
          )}

          {/* Waiting notice for Validation Requested status */}
          {jobData.status === 'Validation Requested' && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarClock className="w-4 h-4 shrink-0" />
              <span>
                Waiting on CORE to approve and complete the job based on your validation request.
              </span>
            </div>
          )}

          {/* Validation Request button for Engineering Completed status */}
          {jobData.status === 'Engineering Completed' && (
            <>
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                onClick={() => setValidationRequestedModalOpen(true)}
              >
                Validation Request
              </Button>
              {showCancelButton && <CancelButton onClick={() => setCancelModalOpen(true)} />}
            </>
          )}

          {/* Simulate Mobile Engineering Complete button + Abort for On Site status */}
          {jobData.status === 'On Site' && (
            <>
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                onClick={() => setEngineeringCompleteModalOpen(true)}
              >
                Simulate Mobile Engineering Complete
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                onClick={() => setAbortModalOpen(true)}
              >
                Abort
              </Button>
              {showCancelButton && <CancelButton onClick={() => setCancelModalOpen(true)} />}
            </>
          )}

          {/* Cancel and Replan buttons for Abort/Aborted state */}
          {jobData.status === 'Aborted' && jobData.jobStatusName === 'Abort' && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                onClick={() => setCancelFromAbortModalOpen(true)}
              >
                Cancel
              </Button>
              <Button variant="outline" size="sm" onClick={() => setReplanFromAbortModalOpen(true)}>
                Replan
              </Button>
            </>
          )}

          {/* Cancelled state notice */}
          {jobData.status === 'Cancelled' && jobData.jobStatusName === 'Cancelled' && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <XCircle className="w-4 h-4 shrink-0" />
              <span>This job has been cancelled and cannot be worked on.</span>
            </div>
          )}

          {/* Simulate Mobile On Site button for En Route status */}
          {jobData.status === 'En Route' && (
            <>
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                disabled={engineerOnSite.isPending}
                onClick={() =>
                  engineerOnSite.mutate(Number(jobData.id), {
                    onSuccess: () => {
                      showSuccessToast('Engineer on site');
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.detail(jobData.id),
                      });
                    },
                  })
                }
              >
                {engineerOnSite.isPending ? 'Sending…' : 'Simulate Mobile On Site'}
              </Button>
              {showCancelButton && <CancelButton onClick={() => setCancelModalOpen(true)} />}
            </>
          )}

          {/* Simulate Mobile En Route button for Planned status */}
          {jobData.status === 'Planned' && (
            <>
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                disabled={engineerEnRoute.isPending}
                onClick={() =>
                  engineerEnRoute.mutate(Number(jobData.id), {
                    onSuccess: () => {
                      showSuccessToast('Engineer en route');
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.detail(jobData.id),
                      });
                    },
                  })
                }
              >
                {engineerEnRoute.isPending ? 'Sending…' : 'Simulate Mobile En Route'}
              </Button>
              {showCancelButton && <CancelButton onClick={() => setCancelModalOpen(true)} />}
            </>
          )}

          {/* Schedule + Cancel buttons for Ready for Planning status */}
          {jobData.status === 'Ready for Planning' && (
            <>
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                onClick={() => navigate('/schedule')}
              >
                <CalendarClock className="w-4 h-4 mr-2" />
                Plan
              </Button>
              {showCancelButton && <CancelButton onClick={() => setCancelModalOpen(true)} />}
            </>
          )}

          {/* Action buttons for new and reopened status */}
          {(status === 'new' || status === 'reopened') && (
            <>
              <Button
                size="sm"
                className="text-white"
                style={{ backgroundColor: '#E84A2B' }}
                onClick={() => setAcceptModalOpen(true)}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Accept
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                onClick={() => setRejectModalOpen(true)}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
              {showCancelButton && <CancelButton onClick={() => setCancelModalOpen(true)} />}
            </>
          )}

          {/* Cancel button for Duration Challenged status (no other actions available) */}
          {js === 'On Hold' && wos === 'Duration Challenged' && (
            <CancelButton onClick={() => setCancelModalOpen(true)} />
          )}

          <RequestMoreInfoButton
            mirPendingDirection={jobData.mirPendingDirection}
            onClick={() => setRequestInfoModalOpen(true)}
          />
        </div>
      </div>

      {/* Modals */}
      <RejectPackageModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onSubmit={(reason, _files) => {
          rejectWorkOrders.mutate(
            [{ id: Number(jobData.id), externalId: jobData.externalId ?? '', comments: reason }],
            {
              onSuccess: () => {
                setRejectModalOpen(false);
                showSuccessToast('Job rejected');
                queryClient.invalidateQueries({
                  queryKey: queryKeys.jobs.detail(jobData.id),
                });
              },
            },
          );
        }}
        isSubmitting={rejectWorkOrders.isPending}
        mode="reject"
        targetName={jobData.id}
      />

      <RequestMoreInfoOrReplyModal
        open={requestInfoModalOpen}
        onOpenChange={setRequestInfoModalOpen}
        job={requestInfoJob}
        isSubmitting={isReply ? replyMoreInfo.isPending : requestMoreInfo.isPending}
        onSubmit={handleRequestInfoSubmit}
      />

      <Dialog
        open={validationRequestedModalOpen}
        onOpenChange={(open) => {
          setValidationRequestedModalOpen(open);
          if (!open) {
            setValidationNote('');
            setValidationInvoiceReference('');
            setValidationInvoiceAmount('');
          }
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Validation Request</DialogTitle>
            <DialogDescription>Submit a validation request for this job.</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground block">Note</label>
              <Textarea
                placeholder="Add a note..."
                value={validationNote}
                onChange={(e) => setValidationNote(e.target.value)}
                rows={4}
                className="resize-none"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground block">Invoice</label>
              <div className="flex gap-2">
                <div className="flex-1 space-y-1">
                  <label className="text-xs text-muted-foreground">Reference</label>
                  <Input
                    placeholder="INV-001"
                    value={validationInvoiceReference}
                    onChange={(e) => setValidationInvoiceReference(e.target.value)}
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <label className="text-xs text-muted-foreground">Amount</label>
                  <Input
                    placeholder="0.00"
                    value={validationInvoiceAmount}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d*\.?\d*$/.test(val)) {
                        setValidationInvoiceAmount(val);
                      }
                    }}
                    inputMode="decimal"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setValidationRequestedModalOpen(false);
                setValidationNote('');
                setValidationInvoiceReference('');
                setValidationInvoiceAmount('');
              }}
              disabled={validationRequested.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="text-white"
              style={{ backgroundColor: '#E84A2B' }}
              disabled={validationRequested.isPending}
              onClick={() => {
                const amount =
                  validationInvoiceAmount !== '' ? Number(validationInvoiceAmount) : undefined;
                validationRequested.mutate(
                  {
                    id: Number(jobData.id),
                    note: validationNote,
                    invoiceReference: validationInvoiceReference || undefined,
                    invoiceAmount: amount,
                  },
                  {
                    onSuccess: () => {
                      setValidationRequestedModalOpen(false);
                      setValidationNote('');
                      setValidationInvoiceReference('');
                      setValidationInvoiceAmount('');
                      showSuccessToast('Validation request submitted');
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.detail(jobData.id),
                      });
                    },
                  },
                );
              }}
            >
              {validationRequested.isPending ? 'Submitting…' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={engineeringCompleteModalOpen}
        onOpenChange={(open) => {
          setEngineeringCompleteModalOpen(open);
          if (!open) setEngineeringCompleteNote('');
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Engineering Complete</DialogTitle>
            <DialogDescription>Add a note to complete this job.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-foreground mb-2 block">
              Note <span className="text-red-600">*</span>
            </label>
            <Textarea
              placeholder="Describe the work completed..."
              value={engineeringCompleteNote}
              onChange={(e) => setEngineeringCompleteNote(e.target.value)}
              rows={5}
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEngineeringCompleteModalOpen(false);
                setEngineeringCompleteNote('');
              }}
              disabled={engineeringComplete.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="text-white"
              style={{ backgroundColor: '#E84A2B' }}
              disabled={!engineeringCompleteNote.trim() || engineeringComplete.isPending}
              onClick={() => {
                engineeringComplete.mutate(
                  { id: Number(jobData.id), note: engineeringCompleteNote.trim() },
                  {
                    onSuccess: () => {
                      setEngineeringCompleteModalOpen(false);
                      setEngineeringCompleteNote('');
                      showSuccessToast('Engineering complete');
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.detail(jobData.id),
                      });
                    },
                  },
                );
              }}
            >
              {engineeringComplete.isPending ? 'Submitting…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={abortModalOpen}
        onOpenChange={(open) => {
          setAbortModalOpen(open);
          if (!open) setAbortNote('');
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Abort Job</DialogTitle>
            <DialogDescription>Are you sure you want to abort this job?</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium text-foreground mb-2 block">
              Note <span className="text-red-600">*</span>
            </label>
            <Textarea
              placeholder="Describe the reason for aborting..."
              value={abortNote}
              onChange={(e) => setAbortNote(e.target.value)}
              rows={5}
              className="resize-none"
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAbortModalOpen(false);
                setAbortNote('');
              }}
              disabled={abortWorkOrder.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
              variant="outline"
              disabled={!abortNote.trim() || abortWorkOrder.isPending}
              onClick={() => {
                abortWorkOrder.mutate(
                  { id: Number(jobData.id), note: abortNote.trim() },
                  {
                    onSuccess: () => {
                      setAbortModalOpen(false);
                      setAbortNote('');
                      showSuccessToast('Job aborted');
                      queryClient.invalidateQueries({
                        queryKey: queryKeys.jobs.detail(jobData.id),
                      });
                    },
                  },
                );
              }}
            >
              {abortWorkOrder.isPending ? 'Aborting…' : 'Abort'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelFromAbortModalOpen} onOpenChange={setCancelFromAbortModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Cancel Job</DialogTitle>
            <DialogDescription>
              This will send a cancellation event to CORE and set the job status to Cancelled. Are
              you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCancelFromAbortModalOpen(false)}
              disabled={cancelFromAbort.isPending}
            >
              Back
            </Button>
            <Button
              type="button"
              className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
              variant="outline"
              disabled={cancelFromAbort.isPending}
              onClick={() => {
                cancelFromAbort.mutate(Number(jobData.id), {
                  onSuccess: () => {
                    setCancelFromAbortModalOpen(false);
                    showSuccessToast('Job cancelled');
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.jobs.detail(jobData.id),
                    });
                  },
                });
              }}
            >
              {cancelFromAbort.isPending ? 'Cancelling…' : 'Confirm Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={replanFromAbortModalOpen} onOpenChange={setReplanFromAbortModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Replan Job</DialogTitle>
            <DialogDescription>
              This will set the job status back to Ready for Planning. No event will be sent to
              CORE. Are you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setReplanFromAbortModalOpen(false)}
              disabled={replanFromAbort.isPending}
            >
              Back
            </Button>
            <Button
              type="button"
              className="text-white"
              style={{ backgroundColor: '#E84A2B' }}
              disabled={replanFromAbort.isPending}
              onClick={() => {
                replanFromAbort.mutate(Number(jobData.id), {
                  onSuccess: () => {
                    setReplanFromAbortModalOpen(false);
                    showSuccessToast('Job set to Ready for Planning');
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.jobs.detail(jobData.id),
                    });
                  },
                });
              }}
            >
              {replanFromAbort.isPending ? 'Replanning…' : 'Confirm Replan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={cancelModalOpen} onOpenChange={setCancelModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Cancel Job</DialogTitle>
            <DialogDescription>
              Warning: Cancelling this job is permanent. It cannot be replanned or recovered. Are
              you sure?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setCancelModalOpen(false)}
              disabled={cancelWorkOrderMutation.isPending}
            >
              Go Back
            </Button>
            <Button
              type="button"
              variant="outline"
              className="border-red-600 text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
              disabled={cancelWorkOrderMutation.isPending}
              onClick={() => {
                cancelWorkOrderMutation.mutate(Number(jobData.id), {
                  onSuccess: () => {
                    setCancelModalOpen(false);
                    showSuccessToast('Job Cancelled');
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.jobs.detail(jobData.id),
                    });
                  },
                });
              }}
            >
              {cancelWorkOrderMutation.isPending ? 'Cancelling…' : 'Confirm Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={acceptModalOpen} onOpenChange={setAcceptModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Accept Job</DialogTitle>
            <DialogDescription>Are you sure you want to accept this job?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAcceptModalOpen(false)}
              disabled={acceptWorkOrders.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="text-white"
              style={{ backgroundColor: '#E84A2B' }}
              disabled={acceptWorkOrders.isPending}
              onClick={() => {
                acceptWorkOrders.mutate([Number(jobData.id)], {
                  onSuccess: (resp) => {
                    setAcceptModalOpen(false);
                    const results = resp.results ?? [];
                    const failed = results.filter((r: { success: boolean }) => !r.success);
                    if (failed.length === 0) {
                      showSuccessToast('Job accepted');
                    }
                    queryClient.invalidateQueries({
                      queryKey: queryKeys.jobs.detail(jobData.id),
                    });
                  },
                });
              }}
            >
              {acceptWorkOrders.isPending ? 'Accepting…' : 'Accept'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
